import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { RealtimePostgresChangesPayload, RealtimeChannel } from '@supabase/supabase-js';
import type { EmojiClickData } from 'emoji-picker-react';

export interface MessageReactionEntry {
  imageUrl?: string;
  user_ids: string[];
}

export type MessageReactions = Record<string, MessageReactionEntry>;

export interface UserMessageAttachment {
  id: string;
  kind: 'image' | 'file';
  name: string;
  url: string;
  mime_type?: string;
}

export interface UserMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
  attachments?: UserMessageAttachment[];
  reactions?: MessageReactions | null;
  sender_name?: string;
  recipient_name?: string;
}

export interface UserConnection {
  id: string;
  user_id: string;
  connected_user_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
  connected_user_name?: string;
  connected_user_avatar?: string;
}

export type PresenceStatus = 'online' | 'away' | 'dnd' | 'offline';

export interface UserPresenceMeta {
  user_id: string;
  status: PresenceStatus;
  status_message: string;
  online_at?: string;
}

const PRESENCE_STORAGE_KEY_PREFIX = 'messenger-presence:';

const normalizePresenceStatus = (value: unknown): PresenceStatus => {
  if (value === 'away' || value === 'dnd' || value === 'offline') {
    return value;
  }

  return 'online';
};

const normalizeMessageAttachments = (value: unknown): UserMessageAttachment[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<UserMessageAttachment[]>((accumulator, entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return accumulator;
    }

    const attachment = entry as Record<string, unknown>;
    const kind = attachment.kind === 'image' ? 'image' : attachment.kind === 'file' ? 'file' : null;
    const id = typeof attachment.id === 'string' ? attachment.id : null;
    const name = typeof attachment.name === 'string' ? attachment.name : null;
    const url = typeof attachment.url === 'string' ? attachment.url : null;

    if (!kind || !id || !name || !url) {
      return accumulator;
    }

    accumulator.push({
      id,
      kind,
      name,
      url,
      mime_type: typeof attachment.mime_type === 'string' ? attachment.mime_type : undefined,
    });

    return accumulator;
  }, []);
};

const normalizeMessageReactions = (value: unknown): MessageReactions => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<MessageReactions>((acc, [emoji, reactionValue]) => {
    if (!reactionValue || typeof reactionValue !== 'object' || Array.isArray(reactionValue)) {
      return acc;
    }

    const reactionRecord = reactionValue as Record<string, unknown>;
    const userIds = Array.isArray(reactionRecord.user_ids)
      ? reactionRecord.user_ids.filter((entry): entry is string => typeof entry === 'string')
      : [];

    if (userIds.length === 0) {
      return acc;
    }

    acc[emoji] = {
      imageUrl: typeof reactionRecord.imageUrl === 'string' ? reactionRecord.imageUrl : undefined,
      user_ids: userIds,
    };

    return acc;
  }, {});
};

const getPresenceStorageKey = (userId: string) => `${PRESENCE_STORAGE_KEY_PREFIX}${userId}`;

const getStoredPresence = (userId: string) => {
  if (typeof window === 'undefined') {
    return { status: 'online' as const, statusMessage: '' };
  }

  try {
    const raw = window.localStorage.getItem(getPresenceStorageKey(userId));
    if (!raw) {
      return { status: 'online' as const, statusMessage: '' };
    }

    const parsed = JSON.parse(raw) as { status?: PresenceStatus; status_message?: string };
    return {
      status:
        normalizePresenceStatus(parsed.status) === 'offline'
          ? 'online'
          : (normalizePresenceStatus(parsed.status) as Exclude<PresenceStatus, 'offline'>),
      statusMessage: typeof parsed.status_message === 'string' ? parsed.status_message : '',
    };
  } catch {
    return { status: 'online' as const, statusMessage: '' };
  }
};

const supabaseClient = supabase as any;

export function useUserMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [presenceMeta, setPresenceMeta] = useState<Record<string, UserPresenceMeta>>({});
  const [selfPresenceStatus, setSelfPresenceStatus] = useState<Exclude<PresenceStatus, 'offline'>>('online');
  const [selfPresenceMessage, setSelfPresenceMessage] = useState('');
  const { toast } = useToast();

  const profileCache = useRef(new Map<string, { name: string; avatar?: string }>());
  const presenceChannelRef = useRef<RealtimeChannel | null>(null);
  const selfPresenceStatusRef = useRef<Exclude<PresenceStatus, 'offline'>>('online');
  const selfPresenceMessageRef = useRef('');

  useEffect(() => {
    selfPresenceStatusRef.current = selfPresenceStatus;
  }, [selfPresenceStatus]);

  useEffect(() => {
    selfPresenceMessageRef.current = selfPresenceMessage;
  }, [selfPresenceMessage]);

  const persistPresence = useCallback(
    (status: Exclude<PresenceStatus, 'offline'>, statusMessage: string) => {
      if (!user || typeof window === 'undefined') {
        return;
      }

      window.localStorage.setItem(
        getPresenceStorageKey(user.id),
        JSON.stringify({ status, status_message: statusMessage })
      );
    },
    [user]
  );

  const syncPresenceState = useCallback(() => {
    const channel = presenceChannelRef.current;
    if (!channel) {
      return;
    }

    const state = channel.presenceState();
    const nextOnlineUsers = new Set<string>();
    const nextPresenceMeta: Record<string, UserPresenceMeta> = {};

    Object.entries(state).forEach(([userId, presences]) => {
      nextOnlineUsers.add(userId);
      const latestPresence = (Array.isArray(presences) ? presences[presences.length - 1] : undefined) as
        | { status?: PresenceStatus; status_message?: string; online_at?: string }
        | undefined;

      nextPresenceMeta[userId] = {
        user_id: userId,
        status: normalizePresenceStatus(latestPresence?.status),
        status_message: typeof latestPresence?.status_message === 'string' ? latestPresence.status_message : '',
        online_at: typeof latestPresence?.online_at === 'string' ? latestPresence.online_at : undefined,
      };
    });

    setOnlineUsers(nextOnlineUsers);
    setPresenceMeta(nextPresenceMeta);
  }, []);

  const trackOwnPresence = useCallback(async () => {
    if (!user || !presenceChannelRef.current) {
      return;
    }

    await presenceChannelRef.current.track({
      user_id: user.id,
      online_at: new Date().toISOString(),
      status: selfPresenceStatusRef.current,
      status_message: selfPresenceMessageRef.current.trim(),
    });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSelfPresenceStatus('online');
      setSelfPresenceMessage('');
      return;
    }

    const storedPresence = getStoredPresence(user.id);
    setSelfPresenceStatus(storedPresence.status);
    setSelfPresenceMessage(storedPresence.statusMessage);
  }, [user]);

  const fetchProfileData = useCallback(async (userId: string) => {
    if (profileCache.current.has(userId)) {
      return profileCache.current.get(userId)!;
    }

    const { data: profile, error } = await supabaseClient
      .from('profiles')
      .select('full_name, username, avatar_url')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      console.error('Error fetching profile:', error);
      const fallback = { name: 'Unknown User', avatar: undefined };
      profileCache.current.set(userId, fallback);
      return fallback;
    }

    const result = {
      name: profile.full_name || profile.username || 'Unknown User',
      avatar: profile.avatar_url || undefined,
    };

    profileCache.current.set(userId, result);
    return result;
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!user) return;

    setLoading(true);

    try {
      const { data: rawData, error } = await supabaseClient
        .from('user_messages')
        .select('*')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const data = (rawData ?? []) as Array<Record<string, any>>;
      const userIds = new Set<string>();
      data.forEach((message) => {
        userIds.add(String(message.sender_id));
        userIds.add(String(message.recipient_id));
      });

      await Promise.all(Array.from(userIds).map(fetchProfileData));

      const withNames = data.map((message) => ({
        ...message,
        attachments: normalizeMessageAttachments(message.attachments),
        reactions: normalizeMessageReactions(message.reactions),
        sender_name: profileCache.current.get(String(message.sender_id))?.name,
        recipient_name: profileCache.current.get(String(message.recipient_id))?.name,
      })) as UserMessage[];

      setMessages(withNames);
    } catch (err) {
      console.error('[useUserMessages] Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [user, fetchProfileData]);

  const fetchConnections = useCallback(async () => {
    if (!user) return;

    try {
      const { data: rawData, error } = await supabaseClient
        .from('user_connections')
        .select('*')
        .or(`user_id.eq.${user.id},connected_user_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const data = (rawData ?? []) as Array<Record<string, any>>;
      const userIds = new Set<string>();
      data.forEach((connection) => {
        userIds.add(String(connection.user_id));
        userIds.add(String(connection.connected_user_id));
      });

      await Promise.all(Array.from(userIds).map(fetchProfileData));

      const withNames = data.map((connection) => {
        const otherUserId = connection.user_id === user.id ? connection.connected_user_id : connection.user_id;
        return {
          ...connection,
          status: connection.status as UserConnection['status'],
          connected_user_name: profileCache.current.get(String(otherUserId))?.name,
          connected_user_avatar: profileCache.current.get(String(otherUserId))?.avatar,
        };
      }) as UserConnection[];

      setConnections(withNames);
    } catch (err) {
      console.error('Error fetching connections:', err);
    }
  }, [user, fetchProfileData]);

  useEffect(() => {
    if (!user) {
      setMessages([]);
      setConnections([]);
      setOnlineUsers(new Set());
      setPresenceMeta({});
      presenceChannelRef.current = null;
      return;
    }

    fetchMessages();
    fetchConnections();

    const handleMessageChange = async (payload: RealtimePostgresChangesPayload<any>) => {
      if (payload.eventType === 'INSERT') {
        const newMessage = payload.new as UserMessage;
        if (newMessage.sender_id === user.id || newMessage.recipient_id === user.id) {
          const senderProfile = await fetchProfileData(newMessage.sender_id);
          const recipientProfile = await fetchProfileData(newMessage.recipient_id);
          const messageWithNames: UserMessage = {
            ...newMessage,
            attachments: normalizeMessageAttachments(newMessage.attachments),
            reactions: normalizeMessageReactions(newMessage.reactions),
            sender_name: senderProfile.name,
            recipient_name: recipientProfile.name,
          };

          setMessages((previousMessages) =>
            previousMessages.some((message) => message.id === messageWithNames.id)
              ? previousMessages
              : [...previousMessages, messageWithNames]
          );
        }
      } else {
        fetchMessages();
      }
    };

    const randomId = Math.random().toString(36).substring(2, 9);

    const messagesChannel = supabaseClient
      .channel(`rt-messages-${user.id}-${randomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_messages' }, handleMessageChange)
      .subscribe();

    const connectionsChannel = supabaseClient
      .channel(`rt-connections-${user.id}-${randomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_connections' }, () => {
        fetchConnections();
      })
      .subscribe();

    const presenceChannel = supabaseClient.channel('global-presence', {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    presenceChannelRef.current = presenceChannel;

    presenceChannel
      .on('presence', { event: 'sync' }, syncPresenceState)
      .on('presence', { event: 'join' }, syncPresenceState)
      .on('presence', { event: 'leave' }, syncPresenceState)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await trackOwnPresence();
        }
      });

    return () => {
      presenceChannelRef.current = null;
      supabaseClient.removeChannel(messagesChannel);
      supabaseClient.removeChannel(connectionsChannel);
      supabaseClient.removeChannel(presenceChannel);
    };
  }, [user, fetchMessages, fetchConnections, fetchProfileData, syncPresenceState, trackOwnPresence]);

  const sendMessage = useCallback(
    async (recipientId: string, content: string, attachments: UserMessageAttachment[] = []) => {
      if (!user || !recipientId || recipientId === user.id) return null;

      const trimmedContent = content.trim();
      if (!trimmedContent && attachments.length === 0) {
        return null;
      }

      const isAcceptedConnection = connections.some(
        (connection) =>
          connection.status === 'accepted' &&
          ((connection.user_id === user.id && connection.connected_user_id === recipientId) ||
            (connection.connected_user_id === user.id && connection.user_id === recipientId))
      );

      if (!isAcceptedConnection) {
        toast({
          title: 'Connection required',
          description: 'You can only message accepted connections.',
          variant: 'destructive',
        });
        return null;
      }

      try {
        const { data: newMessage, error } = await supabaseClient
          .from('user_messages')
          .insert({
            sender_id: user.id,
            recipient_id: recipientId,
            content: trimmedContent,
            attachments,
            read: false,
          })
          .select()
          .single();

        if (error || !newMessage) throw error || new Error('Message insert failed');

        return {
          ...newMessage,
          attachments: normalizeMessageAttachments(newMessage.attachments),
          reactions: normalizeMessageReactions(newMessage.reactions),
        } as UserMessage;
      } catch (err) {
        console.error('Error sending message:', err);
        toast({ title: 'Error', description: 'Failed to send message.', variant: 'destructive' });
        return null;
      }
    },
    [connections, toast, user]
  );

  const toggleReaction = useCallback(
    async (messageId: string, emojiData: EmojiClickData) => {
      if (!user) {
        return;
      }

      try {
        const { data, error } = await supabaseClient.rpc('toggle_user_message_reaction', {
          p_message_id: messageId,
          p_emoji: emojiData.emoji,
          p_image_url: emojiData.imageUrl || '',
        });

        if (error) {
          throw error;
        }

        const nextReactions = normalizeMessageReactions(data);
        setMessages((previousMessages) =>
          previousMessages.map((message) =>
            message.id === messageId ? { ...message, reactions: nextReactions } : message
          )
        );
      } catch (err) {
        console.error('Error toggling Messenger reaction:', err);
        toast({ title: 'Error', description: 'Could not add reaction.', variant: 'destructive' });
      }
    },
    [toast, user]
  );

  const markAsRead = useCallback(async (messageId: string) => {
    try {
      const { error } = await supabaseClient.from('user_messages').update({ read: true }).eq('id', messageId);
      if (error) throw error;
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, []);

  const requestConnection = useCallback(
    async (targetUserId: string) => {
      if (!user || !targetUserId) return null;

      try {
        const { data: existing } = await supabaseClient
          .from('user_connections')
          .select('id, status')
          .or(`and(user_id.eq.${user.id},connected_user_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},connected_user_id.eq.${user.id})`)
          .maybeSingle();

        if (existing && (existing.status === 'pending' || existing.status === 'accepted')) {
          toast({
            title: 'Already connected or pending',
            description: 'You already have a connection or pending request with this user.',
          });
          return null;
        }

        const { data, error } = await supabaseClient
          .from('user_connections')
          .insert({ user_id: user.id, connected_user_id: targetUserId, status: 'pending' })
          .select()
          .single();

        if (error) throw error;

        fetchConnections();
        return data as UserConnection;
      } catch (err) {
        console.error('Error creating connection:', err);
        toast({ title: 'Error', description: 'Failed to send connection request.', variant: 'destructive' });
        return null;
      }
    },
    [user, fetchConnections, toast]
  );

  const updateConnectionStatus = useCallback(
    async (connectionId: string, newStatus: 'accepted' | 'declined') => {
      try {
        const { error } = await supabaseClient.from('user_connections').update({ status: newStatus }).eq('id', connectionId);
        if (error) throw error;

        fetchConnections();
        toast({ title: newStatus === 'accepted' ? 'Connection accepted' : 'Connection declined' });
      } catch (err) {
        console.error('Error updating connection status:', err);
        toast({ title: 'Error', description: 'Failed to update connection status.', variant: 'destructive' });
      }
    },
    [fetchConnections, toast]
  );

  const setPresenceStatus = useCallback(
    async (status: Exclude<PresenceStatus, 'offline'>, statusMessage = '') => {
      const nextStatus = status === 'away' || status === 'dnd' ? status : 'online';
      const nextMessage = statusMessage.trim().slice(0, 80);

      setSelfPresenceStatus(nextStatus);
      setSelfPresenceMessage(nextMessage);
      selfPresenceStatusRef.current = nextStatus;
      selfPresenceMessageRef.current = nextMessage;
      persistPresence(nextStatus, nextMessage);

      if (presenceChannelRef.current) {
        await trackOwnPresence();
      }
    },
    [persistPresence, trackOwnPresence]
  );

  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0);
      setPendingConnectionCount(0);
      return;
    }

    const unread = messages.filter((message) => message.recipient_id === user.id && !message.read).length;
    setUnreadMessageCount(unread);

    const pending = connections.filter(
      (connection) => connection.status === 'pending' && connection.connected_user_id === user.id
    ).length;
    setPendingConnectionCount(pending);
  }, [messages, connections, user]);

  return {
    messages,
    connections,
    loading,
    activeConversation,
    setActiveConversation,
    sendMessage,
    toggleReaction,
    markAsRead,
    requestConnection,
    updateConnectionStatus,
    unreadMessageCount,
    pendingConnectionCount,
    onlineUsers,
    presenceMeta,
    selfPresenceStatus,
    selfPresenceMessage,
    setPresenceStatus,
  };
}